import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

import QUERY_TAGS from "../search/queryTags";
import useQuery from "../search/useQuery";
import { getEntry, invalidateTags, updateEntryData } from "../search/queryCache";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../components/Toast";

import { FAVOURITE_TARGETS, withTarget, withoutTarget } from "./favouritesMappers";
import { fetchMyFavourites, setFavourite } from "./favouritesService";

export const SAVED_SUPPLIERS_KEY = `favourites:mine:${FAVOURITE_TARGETS.supplier}`;

const EMPTY_IDS = [];

const pendingListeners = new Set();
let pendingIds = new Set();

const setPending = (supplierId, pending) => {
  const next = new Set(pendingIds);

  if (pending) next.add(supplierId);
  else next.delete(supplierId);

  pendingIds = next;
  pendingListeners.forEach((listener) => listener());
};

const subscribePending = (listener) => {
  pendingListeners.add(listener);

  return () => pendingListeners.delete(listener);
};

const getPendingIds = () => pendingIds;

const fetchSavedSuppliers = () =>
  fetchMyFavourites({ targetType: FAVOURITE_TARGETS.supplier });

const savedPage = () => getEntry(SAVED_SUPPLIERS_KEY)?.data;

const isInIndex = (supplierId) =>
  Boolean(savedPage()?.items.some((item) => item.targetId === supplierId));

const patchIndex = ({ supplierId, saved }) => {
  const target = { targetId: supplierId, targetType: FAVOURITE_TARGETS.supplier };
  const position = savedPage()?.items.findIndex((item) => item.targetId === supplierId) ?? -1;

  updateEntryData(SAVED_SUPPLIERS_KEY, (page) =>
    saved ? withTarget(page, target) : withoutTarget(page, supplierId)
  );

  return () =>
    updateEntryData(SAVED_SUPPLIERS_KEY, (page) =>
      saved ? withoutTarget(page, supplierId) : withTarget(page, target, position)
    );
};

const useSupplierSave = ({ enabled = true } = {}) => {
  const favourites = useQuery({
    key: enabled ? SAVED_SUPPLIERS_KEY : null,
    tag: QUERY_TAGS.FAVOURITE_SUPPLIERS,
    enabled,
    fetcher: fetchSavedSuppliers,
  });

  const pending = useSyncExternalStore(subscribePending, getPendingIds);

  const items = favourites.currentData?.items;

  const savedTargetIds = useMemo(
    () => (items ? items.map((item) => item.targetId) : EMPTY_IDS),
    [items]
  );

  const savedIds = useMemo(() => new Set(savedTargetIds), [savedTargetIds]);

  const isSaved = useCallback(
    (supplier) => supplier.saved ?? savedIds.has(supplier.id),
    [savedIds]
  );

  const isPending = useCallback((supplier) => pending.has(supplier.id), [pending]);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const toggleSaved = useCallback(async (supplier) => {
    if (!enabledRef.current || pendingIds.has(supplier.id)) return;

    const next = !(supplier.saved ?? isInIndex(supplier.id));

    setPending(supplier.id, true);

    const undo = patchIndex({ supplierId: supplier.id, saved: next });

    try {
      await setFavourite({
        targetType: FAVOURITE_TARGETS.supplier,
        targetId: supplier.id,
        saved: next,
      });

      invalidateTags(QUERY_TAGS.FAVOURITE_SUPPLIERS);

      if (next) {
        showSuccessToast("Added to your saved suppliers", "Saved");
      } else {
        showInfoToast("Removed from your saved suppliers", "Removed");
      }
    } catch {
      undo();
      showErrorToast(
        next
          ? "Could not save this supplier. Please try again."
          : "Could not remove this supplier. Please try again."
      );
    } finally {
      setPending(supplier.id, false);
    }
  }, []);

  const hasIndex = favourites.currentData !== undefined;

  return useMemo(
    () => ({
      isSaved,
      isPending,
      toggleSaved,
      canSave: enabled,
      savedTargetIds,
      savedCount: savedTargetIds.length,
      isLoading: enabled && !hasIndex && !favourites.isError,
      isFetching: favourites.isFetching,
      isError: favourites.isError,
      error: favourites.error,
      refetch: favourites.refetch,
    }),
    [
      isSaved,
      isPending,
      toggleSaved,
      enabled,
      savedTargetIds,
      hasIndex,
      favourites.isError,
      favourites.isFetching,
      favourites.error,
      favourites.refetch,
    ]
  );
};

export default useSupplierSave;
